import React from "react";
import {
  LegacyCard,
  InlineGrid,
  TextField,
  Select,
  Checkbox,
} from "@shopify/polaris";
import { NameLabelColor } from "../CoatData";

const TshirtForm = ({
  form,
  setForm,
  errors,
  baseUrl,
  setApploader,
  viewOrder,
}) => {
  const handleChange =
    (section, key = null) =>
      (values) => {
        const trimmedKey = key?.trim();
        let value;
        if (Array.isArray(values)) {
          value = values[0];
        } else {
          value = values;
        }
        if (trimmedKey) {
          setForm((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [trimmedKey]: value,
            },
          }));
        } else {
          setForm((prev) => ({
            ...prev,
            [section]: value,
          }));
        }
      };

  const monogramOptions = [
    "Left Chest",
    "Right Chest",
    "Left Waist",
    "Right Waist",
    "Left Sleeve Cuff",
    "Right Sleeve Cuff",
    "Back Top Center",
  ];

  const handleMonogramChange = (label, checked) => {
    setForm((prev) => {
      const currentMonograms = prev.tshirt?.monograms || [];
      const updated = checked
        ? [...currentMonograms, label]
        : currentMonograms.filter((m) => m !== label);

      const updatedDetails = { ...(prev.tshirt?.monogramDetails || {}) };
      if (checked && !updatedDetails[label]) {
        updatedDetails[label] = {
          font: "Caligraphy",
          color: "102 White",
          size: "Regular",
          text: "",
        };
      }
      if (!checked) delete updatedDetails[label];

      return {
        ...prev,
        tshirt: {
          ...prev.tshirt,
          monograms: updated,
          monogramDetails: updatedDetails,
        },
      };
    });
  };

  const handleMonogramFieldChange = (label, key, value) => {
    setForm((prev) => ({
      ...prev,
      tshirt: {
        ...prev.tshirt,
        monogramDetails: {
          ...(prev.tshirt?.monogramDetails || {}),
          [label]: {
            ...((prev.tshirt?.monogramDetails || {})[label] || {}),
            [key]: value,
          },
        },
      },
    }));
  };

  const handleFileUpload = async (label, key, file) => {
    const formData = new FormData();
    formData.append("image", file);
    setApploader(true);
    try {
      const res = await fetch(`${baseUrl}/api/files/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        const uploadedFilename = data.files;
        handleMonogramFieldChange(label, "fileimage", uploadedFilename);
        setApploader(false);
      } else {
        alert("File upload failed");
      }
    } catch (error) {}
  };

  const handleDownload = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop() || "download.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {}
  };

  const sectionKey = "tshirt";
  const monograms = form.tshirt?.monograms || [];
  const monogramDetails = form.tshirt?.monogramDetails || {};

  return (
    <LegacyCard title="T-Shirt & Polo" sectioned>
      <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="300">
        <Select
          label="Collar"
          options={["V-Neck", "Crew Neck", "Polo", "Button Down"]}
          value={form[sectionKey]?.collar || ""}
          onChange={handleChange(sectionKey, "collar")}
          error={errors[sectionKey]?.collar}
        />
      </InlineGrid>
      <div className="multiple_option_checkbox">
        <h3>Add Monogram</h3>
        <InlineGrid columns={{ xs: 2, sm: 2, md: 2, lg: 4 }} gap="100">
          {monogramOptions.map((label) => (
            <Checkbox
              key={label}
              label={label}
              checked={monograms.includes(label)}
              onChange={(val) => handleMonogramChange(label, val)}
            />
          ))}
        </InlineGrid>
      </div>
      {monograms.map((label, index) => (
        <div
          key={`monogram-${label}-${index}`}
          className="monogram_option_list"
        >
          <h3>{`Monogram Details - ${label}`}</h3>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 2, lg: 4 }} gap="400">
            <div>
              <Select
                label="Font"
                options={[
                  "Caligraphy",
                  "Times Roman",
                  "Block",
                  "Script",
                  "Bradley",
                  "Old English",
                  "Custom Upload",
                ]}
                value={monogramDetails[label]?.font || ""}
                onChange={(val) =>
                  handleMonogramFieldChange(label, "font", val)
                }
              />
              {monogramDetails[label]?.font === "Custom Upload" ? (
                <input
                  className="fileUpload"
                  type="file"
                  accept=".jpg, .jpeg, .png"
                  name="image"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleFileUpload(label, "fileimage", file);
                  }}
                />
              ) : null}
              {viewOrder === "yes" &&
                monogramDetails[label]?.fileimage ? (
                  <img
                    className="image_server"
                    onClick={() =>
                      handleDownload(monogramDetails[label]?.fileimage || "")
                    }
                    src={monogramDetails[label].fileimage}
                    alt="Mark Russell"
                  />
                ) : null}
            </div>
            <Select
              label="Color"
              options={NameLabelColor}
              value={monogramDetails[label]?.color || ""}
              onChange={(val) =>
                handleMonogramFieldChange(label, "color", val)
              }
            />
            <Select
              label="Size"
              options={["Regular", "Small", "Large", "Extra Large"]}
              value={monogramDetails[label]?.size || ""}
              onChange={(val) =>
                handleMonogramFieldChange(label, "size", val)
              }
            />
            {monogramDetails[label]?.font !== "Custom Upload" && (
              <TextField
                label="Text"
                value={monogramDetails[label]?.text || ""}
                onChange={(val) =>
                  handleMonogramFieldChange(label, "text", val)
                }
                error={errors.tshirt?.monogramDetails?.[label]?.text}
              />
            )}
          </InlineGrid>
        </div>
      ))}
    </LegacyCard>
  );
};

export default TshirtForm;
